/**
 * Reading the customer's own route.
 *
 * Pure, because it is the thing that decides what "this page" means to the
 * agent, and because the alternative (asking React Router from four different
 * depths) gives a different answer depending on where you ask from. The dock
 * sits above the venue routes, so `useParams` there has no `serial`: the
 * route string is the only place it exists.
 */

/** `/{orgId}/venues/{serial}/…` is the only venue-scoped shape in Connect. */
const VENUE = /\/venues\/([^/?#]+)/

/**
 * The venue serial in a route, if it is a venue page.
 *
 * Not validated against the account: it is only ever used to fill `{serial}`
 * in a link the customer is about to be sent to, and the page itself gates on
 * access. A stale serial takes them to a page that tells them so, which is a
 * far better failure than the agent asking which venue while they are
 * standing in one.
 */
export function readVenueSerial(pathname: string): string | undefined {
  const match = VENUE.exec(pathname)
  const serial = match?.[1]
  return serial && serial.length > 0 ? serial : undefined
}

/**
 * Whether the full Support surface is already on screen.
 *
 * The dock and `/support/cases/:id` would otherwise hold two sockets on the
 * same case and every message would arrive twice. The page wins: it is the
 * bigger surface and the person chose it deliberately.
 */
export function isSupportSurface(pathname: string): boolean {
  return (
    /^\/[^/]+\/support(\/|$)/.test(pathname) ||
    pathname.startsWith('/admin/support')
  )
}

/**
 * Whether the dock may open here at all.
 *
 * It needs an organisation, so every route outside one is out, and the two
 * full-screen surfaces that own the whole viewport (the booking day planner's
 * fast view is not one of them: it scrolls, and the pill floats over it) are
 * handled by `isSupportSurface` instead.
 */
export function canDock(pathname: string, orgId: string | undefined): boolean {
  if (!orgId) return false
  return !isSupportSurface(pathname)
}

/** What goes up with a turn: where they are, and the venue if there is one. */
export function turnContextFor(pathname: string): {
  path: string
  venueSerial?: string
} {
  return { path: pathname, venueSerial: readVenueSerial(pathname) }
}
